import { sendGTMEvent } from "@next/third-parties/google";
import { getSessionId } from "./user-session-id";
import { BffFacility } from "../__generated/beffe-sdk";
import { normalizePhoneNumber } from "./format-phone-number";
import { readActiveBbTestsFromDocument } from "@/lib/bbTests/bbTestsAttribute";
import { mergeBbVariant } from "@/lib/bbTests/bbVariant";

export const dataLayerPush = (eventData: any) => {
  // ADDITIVE, QA-only: the test arm rides on every push as ONE flat
  // `bb_variant` key, resolved here and nowhere else (lib/bbTests/bbVariant.ts):
  // the page-level ids the `?bbtest=` switch renders (the `data-bb-tests`
  // attribute the server stamps on <main>, lib/bbTests/bbTestsAttribute.ts)
  // first, then the compact token a component passed for the arm it knows it
  // is rendering (the `bbVariant` argument of the helpers below, e.g.
  // "contact-first"), each token once. Appended LAST and only when either
  // names an arm: on control, in production (the switch is never set there)
  // and in the Studio editor both are empty and the payload is byte-identical
  // to today's. No event is renamed, no key is reordered, no existing field
  // changes.
  const { bb_variant: componentArm, ...rest } = (eventData ?? {}) as Record<string, unknown>;
  const bbVariant = mergeBbVariant(readActiveBbTestsFromDocument(), componentArm);
  const dataLayerEventData = {
    tag_session_id: getSessionId(),
    platform: "NextJS",
    ...rest,
    ...(bbVariant ? { bb_variant: bbVariant } : {}),
  };
  sendGTMEvent(dataLayerEventData);
};

// --- Types ---

type ContactParams = {
  phoneNumber: string;
  facilityCode?: string;
  facilityName?: BffFacility["name"];
  /**
   * ADDITIVE test-arm marker (the /smile test experiences, 2026-09-17): the
   * compact id of the variant the anchor rendered under, e.g. "phone-first".
   * Rides as a flat `bb_variant` key beside the unchanged contact grammar and
   * is left OFF the payload when empty or unset, so the control and every
   * pre-existing caller emit byte-identical events.
   */
  bbVariant?: string;
};

type CarouselParams = {
  facilityCode?: string;
  facilityName?: BffFacility["name"];
  title?: string;
  position: number;
  deviceType?: string;
};

export type NavigationParams = {
  navigationType: "logo" | "link" | "breadcrumb";
  clickLocation: "header" | "footer" | "page" | "left_side_panel" | "hero" | "banner";
  linkUrl: string;
  linkText: string;
  facilityCode?: string;
  facilityName?: BffFacility["name"];
};

export type ClickToSchedulerParams = {
  linkClickLocation: "header" | "footer" | "page" | "left_side_panel" | "hero";
  linkClickSubLocation?: string;
  ctaType: "card" | "button" | "text";
  linkUrl: string;
  linkText?: string;
  /** Same additive test-arm marker as ContactParams.bbVariant; omitted when unset. */
  bbVariant?: string;
};

type DtsAppointmentStepParams = {
  facilityCode?: string;
  facilityName?: BffFacility["name"];
  state?: string;
};

type AccordionParams = {
  title: string;
  isOpen: boolean;
  facilityCode?: string;
  facilityName?: BffFacility["name"];
};

// --- Payload builders: single source of truth for each event's payload shape ---

export function buildContactPayload({
  phoneNumber,
  facilityCode,
  facilityName,
  bbVariant,
}: ContactParams) {
  return {
    brand: "Aspen Dental",
    phone_number: normalizePhoneNumber(phoneNumber),
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
    ...(bbVariant ? { bb_variant: bbVariant } : {}),
  };
}

export function buildCarouselPayload({
  facilityCode,
  facilityName,
  title,
  position,
  deviceType,
}: CarouselParams) {
  return {
    brand: "aspendental",
    carousel_title: title || "",
    carousel_position: position,
    device_type: deviceType,
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
  };
}

export function buildNavigationPayload({
  navigationType,
  clickLocation,
  linkUrl,
  linkText,
  facilityCode,
  facilityName,
}: NavigationParams) {
  return {
    brand: "Aspen Dental",
    navigation_type: navigationType,
    click_location: clickLocation,
    link_url: linkUrl,
    link_text: linkText,
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
  };
}

export function buildSchedulerPayload({
  linkClickLocation,
  linkClickSubLocation,
  ctaType = "button",
  linkUrl,
  linkText = "Schedule Appointment",
  bbVariant,
}: ClickToSchedulerParams) {
  return {
    brand: "aspendental",
    click_name: "schedule",
    cta_type: ctaType,
    link_click_location: linkClickLocation,
    ...(linkClickSubLocation ? { link_click_sub_location: linkClickSubLocation } : {}),
    page_url: globalThis?.location?.pathname || "/",
    page_name: globalThis?.document?.title || "",
    link_text: linkText?.toLowerCase(),
    link_url: linkUrl,
    page_destination_url: linkUrl,
    page_origin_name: globalThis?.document?.title || "",
    ...(bbVariant ? { bb_variant: bbVariant } : {}),
  };
}

export function buildDtsAppointmentStepPayload({
  facilityCode,
  facilityName,
  state,
}: DtsAppointmentStepParams) {
  return {
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    scheduler_type: "bau",
    brand: "aspendental",
    step_name: "reason_for_visit",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
    state: state || "",
  };
}

export function buildAccordionPayload({
  title,
  isOpen,
  facilityCode,
  facilityName,
}: AccordionParams) {
  return {
    brand: "aspendental",
    accordion_title: title || "",
    accordion_interaction: isOpen ? "open" : "closed",
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
  };
}

// --- Push helpers ---

export const pushContactEvent = (params: ContactParams) => {
  dataLayerPush({ event: "contact", ...buildContactPayload(params) });
};

export const pushNavigationEvent = (params: NavigationParams) => {
  dataLayerPush({ event: "navigation", ...buildNavigationPayload(params) });
};

export const pushClickToSchedulerEvent = (params: ClickToSchedulerParams) => {
  dataLayerPush({
    event: "click_to_scheduler",
    ...buildSchedulerPayload(params),
  });
};

export const pushDtsAppointmentStepEvent = (params: DtsAppointmentStepParams) => {
  dataLayerPush({
    event: "appointment_step",
    ...buildDtsAppointmentStepPayload(params),
  });
};

export const pushNavigationClickEvent = ({
  destinationUrl,
  linkClickLocation,
  linkText,
  facilityCode,
  facilityName,
  bbVariant,
}: {
  destinationUrl: string;
  linkClickLocation: "header" | "footer" | "page";
  linkText: string;
  facilityCode?: string;
  facilityName?: BffFacility["name"];
  /** Same additive test-arm marker as ContactParams.bbVariant; omitted when unset. */
  bbVariant?: string;
}) => {
  dataLayerPush({
    event: "navigation_click_event",
    brand: "Aspen Dental",
    page_destination_url: destinationUrl,
    link_click_location: linkClickLocation,
    link_text: linkText,
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    website_selected_facility_id: facilityCode || "",
    website_selected_facility_name: facilityName || "",
    ...(bbVariant ? { bb_variant: bbVariant } : {}),
  });
};

// --- Aspen legacy appointment_step grammar (scheduling funnel) ---

/**
 * The scheduling-funnel step vocabulary the legacy Aspen scheduler reports to
 * GA4, verbatim from aspen-scheduler lib/constants.ts DataLayerStepNames.
 * GA4 only reports the funnel when the emitted grammar matches this legacy
 * schema exactly; bespoke event names silently never show up, the defect
 * class ClearChoice hit in TAG-110.
 *
 * Two Aspen-specific facts that differ from the ClearChoice port, both read
 * off the legacy machine (aspen-scheduler lib/machines/schedulerMachine.ts):
 * - Aspen INCLUDES brand on appointment_step ("aspendental", or
 *   "mottoaligners" when the visit reason is ClearAligners). The ClearChoice
 *   omit-brand ruling (TAG-129) does not transfer.
 * - Aspen emits NO step_sequence and hoists no lead_id / user_id /
 *   ga_client_id / gclid. Those are ClearChoice-only grammar (TAG-110 /
 *   TAG-117); adding them here would deviate from the legacy schema.
 */
export type AppointmentStepName =
  | "reason_for_visit"
  | "reason_selected"
  | "date_time_selection"
  | "patient_information"
  | "additional_patient_information"
  | "review"
  | "confirmation";

/**
 * Legacy reason id table, verbatim from aspen-scheduler lib/constants.ts
 * REASON_FOR_VISIT_MAPPING, keyed by the DigitalVisitReasonEnum string
 * tokens (the same tokens lib/constants/digitalVisitReasons.ts carries).
 */
const REASON_FOR_VISIT_IDS: Record<string, number> = {
  Emergency: 1,
  BrokenTooth_ToothPain: 2,
  Implants: 3,
  Dentures: 4,
  ClearAligners: 5,
  Other: 6,
  Healthy_Mouth_Movement: 7,
};

export type AppointmentStepContext = {
  /** DigitalVisitReasonEnum token, e.g. "Dentures". Drives brand + reason id. */
  reasonForVisit: string;
  facilityCode?: string;
  facilityName?: string;
  /** Two-letter office state; rides as the legacy wrapper's `state` field. */
  stateCode?: string;
  /** Booking reference from the schedule route; confirmation step only. */
  appointmentId?: string;
  /**
   * Insurance answer captured at the details step; review + confirmation.
   * The legacy dataLayer carries it as the strings "true"/"false" (the radio
   * values in aspen-scheduler PatientInformation/DentalInsurance.tsx).
   */
  hasInsurance?: boolean;
  /**
   * ADDITIVE, opt-in placement identifier (the Three-Step Scheduler preset
   * "Analytics ID" variable, app/components/studio/ThreeStepScheduler/
   * presets.tsx). Not part of the legacy aspen-scheduler grammar - every
   * pre-existing caller omits it, so buildAppointmentStepBase's conditional
   * spread below leaves the field OFF the payload entirely and every
   * existing emitted event stays byte-identical. When a Studio author sets
   * it, it rides as placement_id so Bailey can distinguish two placements of
   * the same locked scheduler preset (e.g. hero vs in-page CTA) in GA4.
   */
  placementId?: string;
  /**
   * ADDITIVE test-arm marker (the /smile test experiences, 2026-09-17): the
   * compact id of the scheduler variant set the step fired under, e.g.
   * "contact-first,grouped-times" (app/smile/schedule/threestep/testVariants.ts
   * describeSchedulerVariant). Rides as a flat `bb_variant` key beside the
   * unchanged appointment_step grammar - the event name, step_name vocabulary
   * and every legacy field stay exactly as they are - and is left OFF the
   * payload when empty or unset, so the control (no variables authored) and
   * every pre-existing caller keep emitting byte-identical events.
   */
  bbVariant?: string;
};

/**
 * The legacy scheduler_type marker, verbatim from the legacy push wrapper
 * (aspen-scheduler lib/utils/dataLayerHelpers.ts): a `?src=ops` visit
 * reports "on page", everything else reports "bau".
 */
export function resolveSchedulerType(): "on page" | "bau" {
  try {
    return new URLSearchParams(globalThis?.location?.search ?? "").get("src") === "ops"
      ? "on page"
      : "bau";
  } catch {
    return "bau";
  }
}

/**
 * Fields every legacy scheduling event carries: the brand rule
 * (schedulerMachine.ts, ClearAligners reports mottoaligners, every other
 * reason reports aspendental), the legacy machine's initial
 * language_preference "en-us" (the widget has no language toggle), and the
 * legacy wrapper fields (page origin, scheduler_type, office state). The
 * facility identity rides as website_selected_* when in hand, mirroring the
 * legacy conditional spreads.
 */
function buildAppointmentStepBase(context: AppointmentStepContext) {
  return {
    brand: context.reasonForVisit === "ClearAligners" ? "mottoaligners" : "aspendental",
    language_preference: "en-us",
    page_origin_name: globalThis?.document?.title || "",
    page_origin_url: globalThis?.location?.href || "",
    scheduler_type: resolveSchedulerType(),
    ...(context.stateCode ? { state: context.stateCode } : {}),
    ...(context.facilityCode ? { website_selected_facility_id: context.facilityCode } : {}),
    ...(context.facilityName ? { website_selected_facility_name: context.facilityName } : {}),
    ...(context.placementId ? { placement_id: context.placementId } : {}),
    ...(context.bbVariant ? { bb_variant: context.bbVariant } : {}),
  };
}

/** reason_for_visit + reason_for_visit_id, emitted together per the legacy events. */
function buildReasonFields(context: AppointmentStepContext) {
  const reasonId = REASON_FOR_VISIT_IDS[context.reasonForVisit];
  return {
    reason_for_visit: context.reasonForVisit,
    ...(reasonId === undefined ? {} : { reason_for_visit_id: reasonId }),
  };
}

/**
 * Push one scheduling-funnel step in the legacy Aspen scheduler's exact GA4
 * grammar: FLAT event "appointment_step" with a legacy step_name. Per-step
 * field deltas follow the legacy emitters verbatim:
 * - reason_for_visit (reasonForVisitState.ts waitingForFacilityAnalytics)
 *   carries the facility identity but NO reason fields.
 * - reason_selected (reasonForVisitState.ts reasonSelect dataLayerPush) carries
 *   the reason fields + facility identity; the emergency page pre-sets the
 *   reason (Emergency, id 1) so this fires once as the flow establishes it.
 * - additional_patient_information (patientInformationState.ts contactInformation
 *   entry) carries the reason fields; per TAG-179 it also rides the appointment
 *   facility identity so the funnel joins the office at the patient-info step.
 * - review and confirmation (appointmentReviewState.ts entry +
 *   schedulerMachine.ts pushConfirmationDataLayer) add appointment_facility_id,
 *   appointment_facility_name and dental_insurance; confirmation adds
 *   appointment_id when the booking route returned a reference.
 * Legacy fields with no widget data source are omitted, never fabricated:
 * lead_score / lead_tier_national / lead_tier_office (no scoring response in
 * the widget's booking chain) and account_id (the schedule route response
 * carries only appointmentNumber).
 * Fully guarded: telemetry is best-effort and must never throw into the
 * booking flow.
 */
export function pushAppointmentStep(
  stepName: AppointmentStepName,
  context: AppointmentStepContext,
): void {
  try {
    dataLayerPush({
      event: "appointment_step",
      step_name: stepName,
      ...buildAppointmentStepBase(context),
      ...(stepName === "reason_for_visit" ? {} : buildReasonFields(context)),
      ...(stepName === "additional_patient_information"
        ? {
            ...(context.facilityCode ? { appointment_facility_id: context.facilityCode } : {}),
            ...(context.facilityName ? { appointment_facility_name: context.facilityName } : {}),
          }
        : {}),
      ...(stepName === "review" || stepName === "confirmation"
        ? {
            ...(context.facilityCode ? { appointment_facility_id: context.facilityCode } : {}),
            ...(context.facilityName ? { appointment_facility_name: context.facilityName } : {}),
            ...(typeof context.hasInsurance === "boolean"
              ? { dental_insurance: context.hasInsurance ? "true" : "false" }
              : {}),
          }
        : {}),
      ...(stepName === "confirmation" && context.appointmentId
        ? { appointment_id: context.appointmentId }
        : {}),
    });
  } catch {
    // Best-effort telemetry: a dataLayer failure must never break scheduling.
  }
}

/**
 * The legacy empty-availability event, verbatim from
 * aspen-scheduler lib/machines/states/dateTimeSelectionState.ts: its own
 * event name (NOT appointment_step) with step_name date_time_selection and
 * the shared legacy fields attached. Fires when a loaded availability window
 * contains no bookable times at all.
 */
export function pushNoAppointmentsAvailable(context: AppointmentStepContext): void {
  try {
    dataLayerPush({
      event: "no_appointments_available_msg",
      step_name: "date_time_selection",
      ...buildAppointmentStepBase(context),
      ...buildReasonFields(context),
    });
  } catch {
    // Best-effort telemetry, same guard as pushAppointmentStep.
  }
}

// --- VWO experiment push ---

export type VWOExperimentDecision = {
  featureFlagKey: string;
  featureKey?: string;
  experimentId?: number;
  experimentKey?: string;
  experimentVariationId?: number;
};

export const pushVwoExperimentEvent = (decision: VWOExperimentDecision) => {
  const { experimentId, experimentVariationId, experimentKey } = decision;

  if (experimentId == null || experimentVariationId == null) return;

  dataLayerPush({
    event: "vwo-data-push-ga4",
    vwo_exp_variant_string: `VWO-${experimentId}-${experimentVariationId}`,
    vwo_feature_key: decision.featureKey ?? decision.featureFlagKey,
    vwo_experiment_id: experimentId,
    vwo_experiment_key: experimentKey,
    vwo_experiment_variation_id: experimentVariationId,
  });
};

export const pushInvocaPageLoadEvent = ({ invocaPhoneNumber }: { invocaPhoneNumber: string }) => {
  dataLayerPush({
    event: "invoca_page_load",
    brand: "Aspen Dental",
    invoca_phone_number: invocaPhoneNumber,
    page_origin_name: globalThis?.document?.title || "",
  });
};
